import "./App.scss";
import "./styles/fixed-modal.scss";
import { useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { AssetTable, SignInView, TicketTable, Dashboard } from "./views";
import { AuthCallbackView } from "./views/auth-callback-view";
import { UsersTable } from "./views/users-table";
import { Views } from "./utils/types";
import { SidebarLayout } from "./components/sidebar-layout";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import { CheatSheet } from "./views/cheat-sheet";
import { darkTheme, lightTheme, themeOptions } from "./utils/theme-objects";
import { AssetTableContextProvider } from "./hooks/use-table-context/asset";
import { ProfileView } from "./views/profile-view";
import { ReportingView } from "./views/reporting";
import { UserContextProvider } from "./hooks/use-auth-context";
import { QuaggaView } from "./views/quagga";
import { WidgetContextProvider } from "./hooks/use-widget-context";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { MultiSiteContextProvider } from "./hooks/use-site-context";
import { AssetConfigContextProvider } from "./hooks/use-config-context/asset";
import { UserConfigContextProvider } from "./hooks/use-config-context/user";
import { ApolloClient, ApolloProvider, InMemoryCache, HttpLink } from "@apollo/client";
import { getSiteGraphqlUrl } from "./hooks/use-site-context/utils";
import { ErrorBoundary } from "./components/error-boundary";
import { ScreenAlertProvider } from "./hooks/use-screen-alert";
import { ErrorView } from "./components/error-boundary/error-view";
import { UserPermissionGate } from "./components/user-permission-gate";
import { NoPermissionMessage } from "./components/no-permission-message";
import { ResetPasswordView } from "./views/reset-password";
import { CapabilitiesView } from "./views/capabilities";
import { TicketConfigContextProvider } from "@hooks/use-config-context/ticket";

const theme = createTheme({
    ...themeOptions,
    colorSchemes: {
        dark: darkTheme,
        light: lightTheme,
    },
});

function App() {
    const client = useMemo(() => {
        const uri = getSiteGraphqlUrl();

        return new ApolloClient({
            link: new HttpLink({ uri }),
            cache: new InMemoryCache(),
        });
    }, []);

    // AssetTable/TicketTable each mount their own AssetTableContextProvider/
    // TicketTableContextProvider internally (instanceId="page") — wrapping them
    // again here would mount a second, unconsumed provider that duplicates the
    // list fetch (and now the filter-persistence read/write) for nothing.
    const assetTableElement = (
        <UserPermissionGate caps="read_asset" fallback={<NoPermissionMessage />}>
            <AssetTable />
        </UserPermissionGate>
    );

    const ticketTableElement = (
        <UserPermissionGate caps="read_ticket" fallback={<NoPermissionMessage />}>
            <TicketTable />
        </UserPermissionGate>
    );


    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <div className="App">
                <BrowserRouter>
                    <ScreenAlertProvider>
                        <MultiSiteContextProvider>
                            <ApolloProvider client={client}>
                                <AssetConfigContextProvider>
                                    <TicketConfigContextProvider>
                                        <ErrorBoundary>
                                            <UserContextProvider>
                                                <UserConfigContextProvider>
                                                    <Routes>
                                                        <Route
                                                            path={Views.HOME}
                                                            element={
                                                                <Navigate
                                                                    to={
                                                                        Views.ASSET_TABLE
                                                                    }
                                                                    replace
                                                                />
                                                            }
                                                        />
                                                        <Route
                                                            path={Views.SIGN_IN}
                                                            element={<SignInView />}
                                                        />
                                                        <Route
                                                            path="/auth/:provider/callback"
                                                            element={<AuthCallbackView />}
                                                        />
                                                        <Route
                                                            path={Views.FALLBACK}
                                                            element={<ErrorView />}
                                                        />
                                                        <Route
                                                            element={<SidebarLayout />}
                                                        >
                                                            {/* Asset Table Route w/out assetTag */}
                                                            <Route
                                                                path={Views.ASSET_TABLE}
                                                                element={assetTableElement}
                                                            />

                                                            {/* AssetTable Route with assetTag */}
                                                            <Route
                                                                path={`${Views.ASSET_TABLE}/:assetTag`}
                                                                element={assetTableElement}
                                                            />

                                                            <Route
                                                                path={
                                                                    Views.DASHBOARD
                                                                }
                                                                element={
                                                                    <UserPermissionGate caps="view_dashboard" fallback={<NoPermissionMessage />}>
                                                                        {/* Each AssetTableWidget/TicketTableWidget mounts its own
                                                                            table context provider — no page-level provider needed here. */}
                                                                        <WidgetContextProvider>
                                                                            <DndProvider
                                                                                backend={
                                                                                    HTML5Backend
                                                                                }
                                                                            >
                                                                                <Dashboard />
                                                                            </DndProvider>
                                                                        </WidgetContextProvider>
                                                                    </UserPermissionGate>
                                                                }
                                                            />

                                                            {/* Ticket Table Route w/out Ticket Number*/}
                                                            <Route
                                                                path={Views.TICKET_TABLE}
                                                                element={ticketTableElement}
                                                            />

                                                            {/* Ticket Table Route with Ticket Number */}
                                                            <Route
                                                                path={`${Views.TICKET_TABLE}/:ticketNumber`}
                                                                element={ticketTableElement}
                                                            />


                                                            <Route
                                                                path={Views.PROFILE}
                                                                element={
                                                                    <ProfileView />
                                                                }
                                                            />

                                                            <Route
                                                                path={Views.CHEAT_SHEET}
                                                                element={<CheatSheet />}
                                                            />

                                                            {/* INSIGHTS route commented out — no sidebar link, no cap assigned yet
                                                            <Route
                                                                path={Views.INSIGHTS}
                                                                element={
                                                                    <AssetTableContextProvider>
                                                                        <InsightsView />
                                                                    </AssetTableContextProvider>
                                                                }
                                                            />
                                                            */}

                                                            <Route
                                                                path={Views.QUAGGA}
                                                                element={
                                                                    <AssetTableContextProvider instanceId="quagga">
                                                                        <QuaggaView />
                                                                    </AssetTableContextProvider>
                                                                }
                                                            />


                                                            <Route
                                                                path={Views.REPORTING}
                                                                element={
                                                                    <UserPermissionGate caps="read_reports" fallback={<NoPermissionMessage />}>
                                                                        <ReportingView />
                                                                    </UserPermissionGate>
                                                                }
                                                            />

                                                            <Route
                                                                path={Views.USER_TABLE}
                                                                element={
                                                                    <UserPermissionGate caps="list_users" fallback={<NoPermissionMessage />}>
                                                                        <UsersTable />
                                                                    </UserPermissionGate>
                                                                }
                                                            />

                                                            <Route
                                                                path={`${Views.USER_TABLE}/:wpUserId`}
                                                                element={
                                                                    <UserPermissionGate caps="list_users" fallback={<NoPermissionMessage />}>
                                                                        <UsersTable />
                                                                    </UserPermissionGate>
                                                                }
                                                            />

                                                            <Route
                                                                path={Views.CAPABILITIES}
                                                                element={
                                                                    <UserPermissionGate caps="manage_capabilities" fallback={<NoPermissionMessage />}>
                                                                        <CapabilitiesView />
                                                                    </UserPermissionGate>
                                                                }
                                                            />
                                                        </Route>

                                                        <Route
                                                            path={Views.SIGN_IN}
                                                            element={<SignInView />}
                                                        />

                                                        <Route
                                                            path={Views.RESET_PASSWORD}
                                                            element={
                                                                <ResetPasswordView />
                                                            }
                                                        />
                                                    </Routes>
                                                </UserConfigContextProvider>
                                            </UserContextProvider>
                                        </ErrorBoundary>
                                    </TicketConfigContextProvider>
                                </AssetConfigContextProvider>
                            </ApolloProvider>
                        </MultiSiteContextProvider>
                    </ScreenAlertProvider>
                </BrowserRouter>
            </div>
        </ThemeProvider>
    );
}

export default App;
